import React, { useState, useMemo } from 'react';
import { Spinner, Row, Col, Input, Label, Button, FormGroup, Card } from 'reactstrap';
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
  RSelect,
} from '../../../../components/Component'; // Adjusted path
import { useQuery } from 'react-query';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { Link, useNavigate } from 'react-router-dom';
import { LeadSourceType, LeadStatus } from '../../../../utils/envConfig';
import { timeAsMilliseconds } from '../../../../utils/Utils';

// Lead source type options
const leadSourceTypeOptions = [
  { value: 'all', label: 'All Types' },
  { value: LeadSourceType.HOT, label: 'Hot' },
  { value: LeadSourceType.COLD, label: 'Cold' },
];

// Lead status options
const leadStatusOptions = [
  { value: 'all', label: 'All Statuses' },
  ...Object.values(LeadStatus).map(value => ({ value, label: value })),
];

// Mock LeadSkeletonLoader (as provided in the original snippet)
const LeadSkeletonLoader = ({ columns }) => (
  <tr className='bg-white' style={{ textAlign: "center", verticalAlign: "middle" }}>
    <td colSpan={columns} className="bg-white text-center py-4 px-2">
      <Spinner size="sm" /> Loading leads...
    </td>
  </tr>
);

// Function to build the API URL with all filters
const buildLink = (agentId, filters = {}, page = 1, limit = 5) => {
  let link = `/search/user/filter?page=${page}&sort_by=createdAt&sort_dir=DESC&per_page=${limit}`;
  if (agentId) {
    link = link.concat(`&agent_id=${agentId}`);
  }
  if (filters.source && filters.source !== 'all') {
    link = link.concat(`&lead_source_many=${encodeURIComponent(filters.source.trim())}`);
  }
  if (filters.sourceType && filters.sourceType !== 'all') {
    link = link.concat(`&lead_source_type=${filters.sourceType}`);
  }
  if (filters.status && filters.status !== 'all') {
    link = link.concat(`&lead_status=${filters.status}`);
  }
  if (filters.search) {
    link = link.concat(`&search=${encodeURIComponent(filters.search.trim())}`);
  }
  link = link.concat(`&active=true`);
  link = link.concat(`&roles[]=User`);
  return link;
};

// API function to fetch leads
const fetchLeads = async (axios, agentId, filters = {}, page = 1, limit = 5) => {
  try {
    let processedFilters = { ...filters };
    if (filters.sourceType === LeadSourceType.HOT) {
      try {
        const hotLeadsResponse = await axios.get('/leadSources/hotleads');
        const hotLeadSources = hotLeadsResponse.data?.data || hotLeadsResponse.data || [];
        if (hotLeadSources.length > 0) {
          processedFilters.source = hotLeadSources.map(source => source.name).join(',');
        } else {
          // If no hot lead sources are defined, and HOT is selected, effectively no specific source name is passed
          // This might mean no results unless the backend handles empty `lead_source_many` with `lead_source_type=Hot` appropriately.
          // Or, you might want to pass a special value or prevent the query if no sources exist.
          // For now, setting to an empty string will remove the lead_source_many if it was previously set.
          processedFilters.source = '';
        }
      } catch (error) {
        console.error('Error fetching hot lead sources:', error);
        processedFilters.source = ''; // Prevent search if sources can't be fetched
      }
    } else if (filters.sourceType === LeadSourceType.COLD) {
      try {
        const coldLeadsResponse = await axios.get('/leadSources/coldleads');
        const coldLeadSources = coldLeadsResponse.data?.data || coldLeadsResponse.data || [];
        if (coldLeadSources.length > 0) {
          processedFilters.source = coldLeadSources.map(source => source.name).join(',');
        } else {
          processedFilters.source = '';
        }
      } catch (error) {
        console.error('Error fetching cold lead sources:', error);
        processedFilters.source = '';
      }
    }

    const url = buildLink(agentId, processedFilters, page, limit);
    const response = await axios.get(url);
    const result = {
      leads: response.data.findUsersOutput || [],
      totalItems: response.data.filteredCount || 0,
      totalPages: Math.ceil((response.data.filteredCount || 0) / limit),
      currentPage: page,
    };
    return result;
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw error;
  }
};

// Get status badge color
const getStatusBadgeColor = (status) => {
  if (!status) return 'light';
  switch (status.toLowerCase()) {
    case 'converted':
      return 'success';
    case 'in progress':
      return 'primary';
    case 'new':
      return 'info';
    case 'lost':
      return 'danger';
    default:
      return 'secondary';
  }
};

// Render Lead Status Badge Component
const RenderLeadStatusBadge = React.memo(({ status }) => {
  const baseBadgeClass = "badge badge-sm badge-dot has-bg";
  const color = getStatusBadgeColor(status);
  return (
    <span className={`${baseBadgeClass} bg-${color} text-${color}`}>
      {status || 'Unknown'}
    </span>
  );
});

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    return 'Invalid Date';
  }
};

const RecentLeadsTable = ({ agentId }) => {
  const axios = useAxiosPrivate();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    source: 'all',
    sourceType: 'all',
    status: 'all',
    search: '',
  });
  const [searchText, setSearchText] = useState('');

  const { data: leadSourcesData } = useQuery(
    ['leadSources'],
    async () => {
      const response = await axios.get('/leadSources', { params: { limit: 1000 } });
      return response.data?.data || [];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: timeAsMilliseconds({ hours: 1 }),
      keepPreviousData: true,
    }
  );

  const leadSourceOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Sources' }];
    if (leadSourcesData && leadSourcesData.length > 0) {
      leadSourcesData.forEach(source => {
        options.push({
          value: source.id || source._id, // Use ID for value
          label: source.name,
        });
      });
    }
    return options;
  }, [leadSourcesData]);

  const memoizedFilters = useMemo(() => ({
    source: filters.source,
    sourceType: filters.sourceType,
    status: filters.status,
    search: filters.search,
  }), [filters.source, filters.sourceType, filters.status, filters.search]);

  const {
    data,
    isLoading, // True on initial load or when no data is kept
    isFetching, // True whenever a fetch is in progress (initial or refetch)
    isError,
    error
  } = useQuery(
    ['agentLeads', agentId, JSON.stringify(memoizedFilters), currentPage, itemsPerPage],
    () => fetchLeads(axios, agentId, memoizedFilters, currentPage, itemsPerPage),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
      enabled: !!agentId, // Only run query if agentId is available
      retry: 1,
    }
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleSourceChange = (selectedOption) => {
    setFilters(prev => ({ ...prev, source: selectedOption.label, sourceType: 'all' })); // Reset sourceType if a specific source is picked
    setCurrentPage(1);
  };

  const handleSourceTypeChange = (selectedOption) => {
    // If a specific source type (Hot/Cold) is selected, we might want to clear the individual source filter
    // as fetchLeads will fetch all sources of that type.
    // If 'all' types is selected, the individual source filter (filters.source) can remain.
    setFilters(prev => ({
      ...prev,
      sourceType: selectedOption.value,
      // Clear specific source if Hot/Cold is selected, otherwise keep it
      source: selectedOption.value === LeadSourceType.HOT || selectedOption.value === LeadSourceType.COLD ? 'all' : prev.source
    }));
    setCurrentPage(1);
  };

  const handleStatusChange = (selectedOption) => {
    setFilters(prev => ({ ...prev, status: selectedOption.value }));
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchText }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      source: 'all',
      sourceType: 'all',
      status: 'all',
      search: '',
    });
    setSearchText('');
    setCurrentPage(1);
  };

  const tableColumns = 7;

  return (
    <div id="agent-leads">
      <Block className="mt-4">
        <BlockHead size={'lg'}>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h5">Agent Leads</BlockTitle>
              <BlockDes>
                <p>The agent's most recent leads.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate(`/leads`);
                }}
              >
                <Icon name={'eye'} />
                <span className="d-none d-sm-block">View All Leads</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Block>

      {/* Filter Section */}
      <Block>
        <div className="mb-4 pb-3 border-bottom">
          <Row className="g-3">
            <Col md="3">
              <FormGroup>
                <Label for="leadSource" className="form-label">Lead Source</Label>
                <RSelect
                  options={leadSourceOptions}
                  placeholder="Select Source"
                  value={
                    filters.source === 'all'
                      ? leadSourceOptions.find(option => option.value === 'all')
                      : leadSourceOptions.find(option => option.label === filters.source) || null
                  }
                  onChange={handleSourceChange}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={filters.sourceType === LeadSourceType.HOT || filters.sourceType === LeadSourceType.COLD} // Disable if Hot/Cold type is selected
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="leadSourceType" className="form-label">Lead Source Type</Label>
                <RSelect
                  options={leadSourceTypeOptions}
                  placeholder="Select Source Type"
                  value={leadSourceTypeOptions.find(option => option.value === filters.sourceType)}
                  onChange={handleSourceTypeChange}
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="leadStatus" className="form-label">Lead Status</Label>
                <RSelect
                  options={leadStatusOptions}
                  placeholder="Select Status"
                  value={leadStatusOptions.find(option => option.value === filters.status)}
                  onChange={handleStatusChange}
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="searchText" className="form-label">Search</Label>
                <div className="d-flex">
                  <Input
                    type="text"
                    id="searchText"
                    placeholder="Name, email, phone"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button color="primary" className="ms-2" onClick={handleSearch}>
                    <Icon name="search"></Icon>
                  </Button>
                </div>
              </FormGroup>
            </Col>
          </Row>
          <div className="d-flex justify-content-end mt-2">
            <Button color="light" size="sm" onClick={handleClearFilters}>
              <Icon name="reload-alt" className="me-1"></Icon> Clear Filters
            </Button>
          </div>
        </div>
      </Block>

      {/* Leads Table Block */}
      <Block>
        {/* Show table structure (header) if not an error and (either fetching or already has data) */}
        {/* This ensures header is visible during loading of new/filtered data if old data was present */}
        {!isError && (isFetching || (data?.leads && data.leads.length > 0)) ? (
          <div className="border rounded bg-white">
            <table className="table table-orders">
              <thead className="tb-odr-head">
                <tr className="tb-odr-item">
                  <th className="tb-odr-info text-start ps-4"><span>Name</span></th>
                  <th className="tb-odr-info d-none d-md-table-cell text-start"><span>Contact</span></th>
                  <th className="tb-odr-info d-none d-lg-table-cell text-start"><span>Source</span></th>
                  <th className="tb-odr-info d-none d-lg-table-cell text-center"><span>Source Type</span></th>
                  <th className="tb-odr-info d-none d-sm-table-cell text-center"><span>Date</span></th>
                  <th className="tb-odr-info text-center"><span>Status</span></th>
                  <th className="tb-odr-info text-center"><span>Action</span></th>
                </tr>
              </thead>
              <tbody className="tb-odr-body">
                {isFetching ? (
                  <LeadSkeletonLoader columns={tableColumns} />
                ) : (
                  data?.leads.map((lead) => {
                    const leadId = lead.id || lead._id;
                    const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A';
                    const leadEmail = lead.email;
                    const leadPhone = lead.phone;
                    const leadSource = lead.lead_source || 'N/A';
                    const leadSourceTypeDisplay = lead.leadSourceData?.leadSourceType;
                    const leadStatus = lead.lead_status;
                    const leadDate = lead.createdAt || lead.updatedAt;

                    return (
                      <tr className="tb-odr-item" key={leadId}>
                        <td className="tb-odr-info bg-white text-start ps-4">
                          <Link to={`/leads/${leadId}`} className="fw-medium">
                            {leadName}
                          </Link>
                        </td>
                        <td className="tb-odr-info bg-white d-none d-md-table-cell text-start">
                          <div className="small">{leadEmail || 'N/A'}</div>
                          <div className="small text-muted">{leadPhone || 'N/A'}</div>
                        </td>
                        <td className="tb-odr-info bg-white d-none d-lg-table-cell text-start">
                          <span className="tb-odr-id text-wrap">{leadSource}</span>
                        </td>
                        <td className="tb-odr-info bg-white d-none d-lg-table-cell text-center">
                          {leadSourceTypeDisplay && (
                            <span className={`badge badge-dim badge-pill ${leadSourceTypeDisplay.toLowerCase() === LeadSourceType.HOT.toLowerCase() ? 'bg-danger-dim text-danger' : 'bg-info-dim text-info'
                              }`}>
                              {leadSourceTypeDisplay}
                            </span>
                          )}
                        </td>
                        <td className="tb-odr-info bg-white d-none d-sm-table-cell text-center">
                          <span className="tb-odr-id">
                            {formatDate(leadDate)}
                          </span>
                        </td>
                        <td className="tb-odr-info bg-white text-center">
                          <RenderLeadStatusBadge status={leadStatus} />
                        </td>
                        <td className="tb-odr-info bg-white text-center">
                          <Link to={`/leads/${leadId}`} className="btn btn-sm btn-icon btn-light me-1">
                            <Icon name="eye"></Icon>
                          </Link>
                          <Link to={`/leads/${leadId}/edit`} className="btn btn-sm btn-icon btn-light">
                            <Icon name="edit"></Icon>
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Show "No leads" message if not fetching, not an error, and no leads exist (after fetch attempt) */}
        {/* `isLoading` is also checked to prevent showing this before the initial load attempt if `agentId` is initially null */}
        {!isFetching && !isError && (!data?.leads || data.leads.length === 0) && !isLoading && agentId ? (
          <Card className="p-4">
            <p className="text-center fs-6 fw-bold">
              No leads found matching your criteria.
            </p>
          </Card>
        ) : null}

        {/* Show error message if an error occurred */}
        {isError && (
          <Card className="p-4">
            <p className="text-center fs-6 fw-bold text-danger">
              Error loading leads: {error?.message || 'Unknown error'}
            </p>
          </Card>
        )}

        {/* Show pagination if not fetching, not an error, leads exist, and there's more than one page */}
        {!isFetching && !isError && data?.leads && data.leads.length > 0 && data?.totalPages > 1 && (
          <div className="mt-3">
            <PaginationComponent
              itemPerPage={itemsPerPage}
              totalItems={data?.totalItems || 0}
              currentPage={currentPage}
              paginate={paginate}
            />
          </div>
        )}
      </Block>
    </div>
  );
};

export default RecentLeadsTable;
